/**
 * The way into the angle-review queue, on the two screens that had none.
 *
 * `ViewpointCoveragePanel` links to it twice and `SessionScreen` once, each
 * beside a specific reason to go there. The other two coverage screens - the
 * head report and the scene report - never did, so a customer whose project
 * was `human_head` or `human_in_environment` could only reach the queue by
 * typing its URL, which for a customer is the same as it not existing. Found
 * in a browser: eight unmarked photographs in a scene project, and nothing on
 * the coverage screen mentioning the screen built for exactly that.
 *
 * Three rules it follows, none of them cosmetic:
 *
 * * **It shows a count from the server, across the whole project.** Not the
 *   length of a loaded page. A project can hold more photographs than one
 *   request returns, and "8 bekliyor" for a project holding 138 is the media
 *   count defect again in a new place.
 * * **Nothing waiting, nothing shown.** An always-present link to an empty
 *   queue teaches people to ignore it.
 * * **A failed read renders nothing.** This is a signpost beside the coverage
 *   report, not the report; an error box here would be a screenful of alarm
 *   about a link.
 */

import { useCallback } from "react";
import { Link } from "react-router-dom";

import { getRegionReview } from "@/api/client";
import { paths } from "@/app/paths";
import { useAsync } from "@/lib/useAsync";

export function RegionReviewLink({ projectId }: { projectId: string }) {
  const fetcher = useCallback(
    // Wrapped so that *nothing* here can throw into the coverage screen. This
    // is a signpost beside the report; a signpost that can take down the
    // report is worse than no signpost, and a rejected promise is a link that
    // does not draw rather than a blank page.
    (signal: AbortSignal) => {
      try {
        return getRegionReview(projectId, { limit: 1 }, { signal });
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [projectId],
  );
  const { state } = useAsync(fetcher);

  const remaining = state.data?.remaining ?? 0;
  if (remaining === 0) return null;

  return (
    <div className="card row" style={{ justifyContent: "space-between", gap: "1rem" }}>
      <div className="stack-tight">
        <strong>{remaining} fotoğrafın çekim açısı işaretlenmedi</strong>
        <span className="muted">
          İşaretlenmeyen fotoğraflar hiçbir açının kanıtı sayılmaz; kapsama
          onlarsız hesaplanır.
        </span>
      </div>
      <Link className="btn btn--secondary" to={paths.projectRegionReview(projectId)}>
        Açı Kontrolü
      </Link>
    </div>
  );
}
